"use client"
import React, { useRef, useState, useEffect } from 'react'
import { apiGetAllKeywords, apiSearchResult } from '@/apis/user'
import { ellipse, getBaseImageUrl, getCategoryHref, getMerchantHref } from '@/constants/hooks'
import { MetaKeywordsResponse, SearchCategories, SearchMerchant } from '@/services/dataTypes'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'

interface Props {
    companyId: string
    mer_slug: string
    slug_type: string
    cat_slug: string
    domain: string
}

const SearchBar = ({ companyId, mer_slug, slug_type, cat_slug, domain }: Props) => {
    const searchParams = useSearchParams()
    const params = new URLSearchParams(searchParams)
    const [search, setSearch] = useState<string>("")
    const [isDropdownVisible, setIsDropdownVisible] = useState(false)
    const [tagsData, setTagsData] = useState<any>([])
    const [categoriesData, setCategoriesData] = useState<SearchCategories[]>([])
    const [merchantData, setMerchantData] = useState<SearchMerchant[]>([])
    const [errMsg, setErrMessage] = useState({ show: false, err: false, msg: '' })
    const pathName = usePathname()
    const router = useRouter()
    const inputRef = useRef<HTMLInputElement>(null)
    const [mounted, setMounted] = useState(false)

    useEffect(() => { setMounted(true) }, [])

    const handleKeywords = async () => {
        const response = await apiGetAllKeywords(companyId)
        if (response.data) {
            setTagsData(response.data)
        }
    }

    useEffect(() => {
        setSearch(params.get('query') || "")
    }, [])

    const handleSearch = async () => {
        try {
            if (search.trim().length >= 3) {
                const response = await apiSearchResult(search, companyId)
                if (response.data?.merchants?.length > 0 || response.data?.categories?.length > 0) {
                    setCategoriesData(response.data?.categories || [])
                    setMerchantData(response.data?.merchants || [])
                    setErrMessage({ show: false, err: false, msg: '' })
                } else {
                    setCategoriesData([])
                    setMerchantData([])
                    setErrMessage({ show: true, err: true, msg: 'No result found.' })
                    setTimeout(() => setErrMessage({ show: false, err: false, msg: '' }), 5000)
                }
            } else {
                setCategoriesData([])
                setMerchantData([])
                setErrMessage({ show: true, err: true, msg: 'Input should be at least three characters long.' })
                setTimeout(() => setErrMessage({ show: false, err: false, msg: '' }), 5000)
            }
        } catch (err) {
            console.error('Search error:', err)
        }
    }

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (search.trim() === "") {
                setMerchantData([])
                setCategoriesData([])
                setErrMessage({ show: false, err: false, msg: '' })
            } else {
                handleSearch()
            }
        }, 1000)
        return () => clearTimeout(timeoutId)
    }, [search])

    useEffect(() => {
        const query = params.get('query') || ""
        if (query !== search && document.activeElement !== inputRef.current) {
            setSearch(query)
        }
    }, [params])

    useEffect(() => {
        if (pathName === '/search') setIsDropdownVisible(false)
    }, [pathName])

    const keywordsFetchedRef = useRef(false);

    const fetchKeywordsIfNeeded = () => {
        if (!keywordsFetchedRef.current && companyId) {
            keywordsFetchedRef.current = true;
            handleKeywords();
        }
    };

    const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && search.trim()) {
            e.preventDefault()
            router.push(`/search?query=${encodeURIComponent(search.trim())}`)
            setIsDropdownVisible(false)
        }
    }

    return (
        <div className="relative w-full max-w-2xl mx-auto">
            <div className="relative group">
                <input
                    type="text"
                    placeholder="Search stores, categories..."
                    className="w-full bg-slate-50 border border-slate-100 focus:bg-white focus:border-indigo-100 focus:shadow-[0_8px_30px_rgb(79,70,229,0.08)] px-4 sm:px-6 pl-4 sm:pl-5 pr-11 sm:pr-12 py-2.5 sm:py-3 rounded-full text-base sm:text-sm text-[16px] font-semibold text-slate-900 transition-all duration-300 placeholder:text-slate-400 placeholder:font-medium outline-none"
                    ref={inputRef}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchSubmit}
                    onBlur={() => setTimeout(() => setIsDropdownVisible(false), 150)}
                    onFocus={() => {
                        setIsDropdownVisible(pathName !== '/search');
                        fetchKeywordsIfNeeded();
                    }}
                />
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                        if (search.trim()) {
                            router.push(`/search?query=${encodeURIComponent(search.trim())}`)
                            setIsDropdownVisible(false)
                        }
                    }}
                    aria-label="Search"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-9 sm:h-9 bg-slate-800 hover:bg-indigo-400 active:scale-95 text-white rounded-full transition-all duration-300 flex items-center justify-center shadow-[0_4px_14px_rgba(15,23,42,0.25)] hover:shadow-[0_6px_18px_rgba(79,70,229,0.4)] cursor-pointer z-10 group"
                >
                    <svg className="w-4 h-4 text-white group-hover:scale-110 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7" />
                        <line x1="21" y1="21" x2="16.5" y2="16.5" />
                    </svg>
                </button>
            </div>

            {mounted && isDropdownVisible && (
                <div className="absolute top-[calc(100%+0.5rem)] right-0 left-auto bg-white rounded-2xl md:rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.12)] border border-slate-100 p-3.5 sm:p-5 md:p-6 z-50 max-h-[75vh] md:max-h-[500px] overflow-y-auto w-[calc(100vw-2rem)] min-[400px]:w-[90vw] sm:w-[500px] md:w-[600px] lg:w-[700px] max-w-[calc(100vw-2rem)] scrollbar-hide">
                    {/* Error Message */}
                    {errMsg.show && (
                        <p className="text-xs sm:text-sm text-red-600 font-medium mb-4">{errMsg.msg}</p>
                    )}

                    {/* 1. Stores FIRST */}
                    {merchantData.length > 0 && (
                        <div className="mb-5 sm:mb-6">
                            <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2.5 sm:mb-3">Stores</h5>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                                {merchantData.map((item, i) => (
                                    <Link
                                        key={i}
                                        href={getMerchantHref<SearchMerchant>(item, mer_slug || 'store', slug_type || 'slug')}
                                        onClick={() => setIsDropdownVisible(false)}
                                        className="group flex flex-col items-center p-2.5 sm:p-3 rounded-xl sm:rounded-2xl border border-transparent hover:border-indigo-100 hover:bg-indigo-50/50 hover:shadow-sm transition-all duration-300"
                                    >
                                        <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-[1rem] bg-white border border-slate-100 flex items-center justify-center p-1.5 sm:p-2 mb-2 sm:mb-3 shadow-sm group-hover:scale-105 transition-transform duration-300">
                                            <Image
                                                src={getBaseImageUrl(domain, item.merchant_logo, "")}
                                                alt={item.merchant_name}
                                                width={40}
                                                height={40}
                                                className="object-contain"
                                            />
                                        </div>
                                        <span className="text-[11px] sm:text-xs font-bold text-slate-700 text-center line-clamp-1 group-hover:text-indigo-600 transition-colors">
                                            {item.merchant_name}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 2. Categories SECOND */}
                    {categoriesData.length > 0 && (
                        <div className="mb-5 sm:mb-6">
                            <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2.5 sm:mb-3">Categories</h5>
                            <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                {categoriesData.map((item, i) => (
                                    <Link
                                        key={i}
                                        href={item?.url ? (item.url.startsWith('/') ? item.url : `/${item.url}`) : getCategoryHref(item, cat_slug || 'category', slug_type || 'slug')}
                                        onClick={() => setIsDropdownVisible(false)}
                                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-slate-50 hover:bg-indigo-50 text-slate-600 hover:text-indigo-600 rounded-lg sm:rounded-xl text-[11px] sm:text-xs font-bold transition-all duration-300 hover:scale-[1.02]"
                                    >
                                        {item.name}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 3. Popular Tags (Shown ONLY when input is empty / user hasn't typed anything yet) */}
                    {tagsData.length > 0 && !search.trim() && (
                        <div>
                            <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                                <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest m-0">
                                    Trending Searches
                                </h5>
                            </div>
                            <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                {tagsData.slice(0, 20).map((item: string, i: number) => (
                                    <Link
                                        key={i}
                                        href={`/search?query=${encodeURIComponent(item)}`}
                                        onClick={() => setIsDropdownVisible(false)}
                                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-slate-50 hover:bg-indigo-50 text-slate-600 hover:text-indigo-600 rounded-lg sm:rounded-xl text-[11px] sm:text-xs font-bold transition-all duration-300 hover:scale-[1.02]"
                                    >
                                        {item}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default SearchBar